/**
 * L'édition des règles de correspondance (ARTO-130) : tout ce que l'éditeur
 * calcule sans afficher. Des fonctions pures — un état de règles entre, un
 * état de règles sort — pour que l'écran ne fasse que dessiner.
 *
 * Le moteur de l'API reste seul juge des règles (`reprise/regles.ts`) ; ces
 * contrôles locaux n'en sont que l'avance : montrer une erreur avant l'envoi,
 * et refuser d'enregistrer une date ambiguë sans format.
 */
import type {
  BlocNature,
  ChampRegle,
  CiblePivot,
  ColonneProfilee,
  Dictionnaire,
  FiltreRegle,
  ProfilSources,
  RefSource,
  Regles,
  SourceRegle,
  Surcharges,
  TableProfilee,
  Transformation,
  TypeTransformation,
} from './correspondance.js';
import type { EtatLot, LotReprise, SourceLot } from './reprise.js';

export const ROLE_PRINCIPAL = 'principal';

export function copier<T>(valeur: T): T {
  return structuredClone(valeur);
}

// ── Valeurs de départ ────────────────────────────────────────────────────

export function refVide(champ = '', role: string | null = null): RefSource {
  return { champ, role, parent: false };
}

export function champVide(): ChampRegle {
  return { sources: [], separateur: ' ', transformations: [] };
}

export function blocVide(): BlocNature {
  return { colonnes: {}, cleOrigine: null, filtres: [], dedoublonner: null };
}

export function filtreVide(): FiltreRegle {
  return { source: refVide(), operateur: 'egal', valeur: '' };
}

/** Des règles sans aucun champ : la page blanche d'un lot sans profil de départ. */
export function reglesVides(nature: string): Regles {
  return {
    sources: [{ role: ROLE_PRINCIPAL, feuille: null, racine: null }],
    jointures: [],
    deplier: null,
    filtres: [],
    natures: { [nature]: blocVide() },
  };
}

/** Chaque transformation arrive avec des paramètres déjà valides. */
export function transformationParDefaut(type: TypeTransformation): Transformation {
  switch (type) {
    case 'scinder':
      return { type, separateur: ',', index: 0 };
    case 'date':
      return { type, format: 'JJ/MM/AAAA' };
    case 'montant':
      return { type, decimal: ',', devise: 'EUR' };
    case 'dictionnaire':
      return { type, valeurs: {} };
    case 'constante':
    case 'prefixe':
      return { type, valeur: '' };
    case 'si_vide':
      return { type, valeur: '' };
    case 'concat':
      return { type, source: refVide(), separateur: ' ' };
    default:
      return { type };
  }
}

// ── Dictionnaires saisis en lignes « source = valeur » ───────────────────

export function dictionnaireEnTexte(valeurs: Record<string, string>): string {
  return Object.entries(valeurs)
    .map(([source, cible]) => `${source} = ${cible}`)
    .join('\n');
}

export function texteEnDictionnaire(texte: string): Record<string, string> {
  const valeurs: Record<string, string> = {};
  for (const ligne of texte.split('\n')) {
    const coupure = ligne.indexOf('=');
    if (coupure < 0) continue;
    const source = ligne.slice(0, coupure).trim();
    if (source !== '') valeurs[source] = ligne.slice(coupure + 1).trim();
  }
  return valeurs;
}

// ── Chemins d'erreur ─────────────────────────────────────────────────────

export const cheminChamp = (nature: string, cle: string): string =>
  `natures.${nature}.colonnes.${cle}`;

/** Les erreurs dont le chemin est `prefixe` ou en dessous (`prefixe.…`). */
export function erreursSous<T extends { chemin: string }>(erreurs: readonly T[], prefixe: string) {
  return erreurs.filter(
    (erreur) => erreur.chemin === prefixe || erreur.chemin.startsWith(`${prefixe}.`),
  );
}

// ── Sources du lot, rôles et tables du profil ────────────────────────────

/** Le fichier du lot qui joue chaque rôle : la surcharge, sinon le fichier de même rang. */
export function fichiersDesRoles(
  regles: Regles,
  sources: readonly SourceLot[],
  surcharges: Surcharges,
): Record<string, string | null> {
  const resultat: Record<string, string | null> = {};
  regles.sources.forEach((source, rang) => {
    resultat[source.role] = surcharges.fichiers[source.role] ?? sources[rang]?.fichierId ?? null;
  });
  return resultat;
}

/** La table que la règle désigne : sa feuille ou sa racine, sinon la première. */
export function tableDeLaRegle(
  profil: ProfilSources,
  fichierId: string | null,
  regle: SourceRegle | undefined,
): TableProfilee | null {
  const fichier = profil.fichiers.find((candidat) => candidat.fichierId === fichierId);
  const tables = fichier?.tables ?? [];
  const choix = regle?.feuille ?? regle?.racine ?? null;
  if (choix === null || choix === '') return tables[0] ?? null;
  return tables.find((table) => table.nom === choix) ?? null;
}

export interface ContexteSources {
  regles: Regles;
  profil: ProfilSources | null;
  sources: readonly SourceLot[];
  surcharges: Surcharges;
}

/** La table qui alimente un rôle (`null` : la source principale). */
export function tableDuRole(contexte: ContexteSources, role: string | null): TableProfilee | null {
  if (contexte.profil === null) return null;
  const principal = contexte.regles.sources[0]?.role ?? ROLE_PRINCIPAL;
  const nom = role ?? principal;
  const regle = contexte.regles.sources.find((source) => source.role === nom);
  const fichier = fichiersDesRoles(contexte.regles, contexte.sources, contexte.surcharges)[nom];
  return tableDeLaRegle(contexte.profil, fichier ?? null, regle);
}

export function colonneProfilee(
  contexte: ContexteSources,
  reference: RefSource,
): ColonneProfilee | null {
  if (reference.parent) return null;
  return (
    tableDuRole(contexte, reference.role)?.colonnes.find(
      (colonne) => colonne.nom === reference.champ,
    ) ?? null
  );
}

// ── Dates ambiguës ───────────────────────────────────────────────────────

export interface DateAmbigue {
  /** Le fichier (chemin dans le dépôt) et la colonne que le profil signale. */
  fichier: string;
  colonne: string;
  exemple: string;
}

/** Les colonnes dont le profil dit que jour et mois peuvent s'inverser. */
export function colonnesAmbigues(profil: ProfilSources): DateAmbigue[] {
  return profil.fichiers.flatMap((fichier) =>
    (fichier.tables ?? []).flatMap((table) =>
      table.colonnes
        .filter((colonne) => colonne.format === 'date_ambigue')
        .map((colonne) => ({
          fichier: fichier.chemin,
          colonne: colonne.nom,
          exemple: colonne.frequentes[0]?.valeur ?? '',
        })),
    ),
  );
}

function referencesDuChamp(champ: ChampRegle): RefSource[] {
  const references = [...champ.sources];
  for (const etape of champ.transformations) {
    if ((etape.type === 'concat' || etape.type === 'si_vide') && etape.source)
      references.push(etape.source);
  }
  return references;
}

/** Une colonne lue par ce champ est-elle une date ambiguë ? Laquelle. */
export function colonneAmbigueDuChamp(champ: ChampRegle, contexte: ContexteSources): string | null {
  for (const reference of referencesDuChamp(champ)) {
    if (colonneProfilee(contexte, reference)?.format === 'date_ambigue') return reference.champ;
  }
  return null;
}

/**
 * Les champs qui lisent une colonne ambiguë sans étape `date` : le format
 * source est exigé partout où l'API signale l'ambiguïté. Un champ en règle
 * (une étape `date` qui déclare son format) n'y figure pas.
 */
export function champsSansFormatDate(
  contexte: ContexteSources,
): { chemin: string; colonne: string }[] {
  const manques: { chemin: string; colonne: string }[] = [];
  for (const [nature, bloc] of Object.entries(contexte.regles.natures)) {
    for (const [cle, champ] of Object.entries(bloc.colonnes)) {
      const colonne = colonneAmbigueDuChamp(champ, contexte);
      if (colonne !== null && !champ.transformations.some((etape) => etape.type === 'date'))
        manques.push({ chemin: cheminChamp(nature, cle), colonne });
    }
  }
  return manques;
}

// ── Champs obligatoires ──────────────────────────────────────────────────

export function champAlimente(champ: ChampRegle | undefined): boolean {
  if (!champ) return false;
  return (
    champ.sources.some((reference) => reference.champ.trim() !== '') ||
    champ.transformations.some((etape) => etape.type === 'constante')
  );
}

/** Les colonnes obligatoires du gabarit qu'aucun champ n'alimente encore. */
export function obligatoiresManquantes(bloc: BlocNature, cible: CiblePivot | undefined): string[] {
  if (!cible) return [];
  return cible.colonnes
    .filter((colonne) => colonne.obligatoire && !champAlimente(bloc.colonnes[colonne.cle]))
    .map((colonne) => colonne.cle);
}

/** Les règles telles que l'API les attend : un champ sans source ni constante n'est pas envoyé. */
export function reglesAEnvoyer(regles: Regles): Regles {
  const nettoyees = copier(regles);
  for (const bloc of Object.values(nettoyees.natures)) {
    for (const [cle, champ] of Object.entries(bloc.colonnes)) {
      champ.sources = champ.sources.filter((reference) => reference.champ.trim() !== '');
      if (!champAlimente(champ) && champ.transformations.length === 0) delete bloc.colonnes[cle];
    }
    if (bloc.cleOrigine !== null && bloc.cleOrigine.champ.trim() === '') bloc.cleOrigine = null;
    bloc.filtres = bloc.filtres.filter((filtre) => filtre.source.champ.trim() !== '');
  }
  nettoyees.filtres = nettoyees.filtres.filter((filtre) => filtre.source.champ.trim() !== '');
  nettoyees.jointures = nettoyees.jointures.filter(
    (jointure) => jointure.cle.trim() !== '' && jointure.sur.trim() !== '',
  );
  return nettoyees;
}

/** Les surcharges telles que l'API les attend : sans entrée vide. */
export function surchargesAEnvoyer(surcharges: Surcharges): Surcharges {
  const nettoyees = copier(surcharges);
  for (const [cle, dictionnaire] of Object.entries(nettoyees.dictionnaires)) {
    if (Object.keys(dictionnaire.valeurs).length === 0 && !dictionnaire.defaut)
      delete nettoyees.dictionnaires[cle];
  }
  for (const [cle, valeur] of Object.entries(nettoyees.constantes)) {
    if (valeur === '') delete nettoyees.constantes[cle];
  }
  return nettoyees;
}

export function surchargesVides(surcharges: Surcharges): boolean {
  const nettoyees = surchargesAEnvoyer(surcharges);
  return (
    Object.keys(nettoyees.fichiers).length === 0 &&
    Object.keys(nettoyees.dictionnaires).length === 0 &&
    Object.keys(nettoyees.constantes).length === 0
  );
}

export function dictionnaireVide(): Dictionnaire {
  return { valeurs: {} };
}

// ── Étapes d'un lot à correspondance ─────────────────────────────────────

export const ETAPES_LOT = [
  'depose',
  'profile',
  'correspondance',
  'transforme',
  'essai',
  'a_lancer',
] as const;
export type EtapeLot = (typeof ETAPES_LOT)[number];
export type EtatEtape = 'fait' | 'courante' | 'a_venir';

const ETATS_LANCES: readonly EtatLot[] = ['enfile', 'en_cours', 'termine', 'scelle'];

/** Où en est le lot : chaque étape est faite, courante (la première non faite) ou à venir. */
export function etapesDuLot(
  lot: Pick<LotReprise, 'etat' | 'profil' | 'profilage' | 'transformation' | 'morceaux'>,
): { etape: EtapeLot; etat: EtatEtape }[] {
  const morceaux = lot.morceaux?.liste ?? [];
  const transforme = lot.transformation?.fait === true || lot.etat === 'transforme';
  const faites: Record<EtapeLot, boolean> = {
    depose: true,
    profile: lot.profilage?.fait === true,
    correspondance: lot.profil != null,
    transforme,
    essai: morceaux.length > 0 && morceaux.every((morceau) => morceau.etat !== 'depose'),
    a_lancer:
      morceaux.length > 0 &&
      morceaux.every((morceau) => morceau.etat === 'termine' || morceau.etat === 'scelle'),
  };
  let courante = false;
  return ETAPES_LOT.map((etape) => {
    if (faites[etape]) return { etape, etat: 'fait' as const };
    if (!courante) {
      courante = true;
      return { etape, etat: 'courante' as const };
    }
    return { etape, etat: 'a_venir' as const };
  });
}

/** Combien de morceaux sont lancés (en file, en cours, terminés). */
export function morceauxLances(lot: Pick<LotReprise, 'morceaux'>): number {
  return (lot.morceaux?.liste ?? []).filter((morceau) => ETATS_LANCES.includes(morceau.etat))
    .length;
}
